import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useState, useEffect, React } from "react";
import { Routes, Route, useLocation } from "react-router-dom";

import { T } from "./constants/theme";
import { apiFetch } from "./services/api";
import Footer from "./components/Footer";
import { ToastProvider } from "./contexts/ToastContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CartProvider } from "./contexts/CartContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import { AuthProvider } from "./contexts/AuthContext";
import { PaymentContext } from "./contexts/PaymentContext";
import CartDrawer from "./components/CartDrawer";
import Navbar from "./components/Navbar";
import ScrollToTop from "./components/ScrollToTop";
import { RequireAuth, RequireAdmin, RequireFarmer, RequireSuperAdmin } from "./routes/guards";
import ShopPage from "./pages/customer/ShopPage";
import ProductDetail from "./pages/customer/ProductDetail";
import AuthPage from "./pages/customer/AuthPage";
import ForgotPasswordPage from "./pages/customer/ForgotPasswordPage";
import ResetPasswordPage from "./pages/customer/ResetPasswordPage";
import OrdersPage from "./pages/customer/OrdersPage";
import HomePage from "./pages/customer/HomePage";
import ExplorePage from "./pages/customer/ExplorePage";
import AdvicePage from "./pages/customer/AdvicePage";
import VirtualTryOnPage from "./pages/customer/VirtualTryOnPage";
import ProfilePage from "./pages/customer/ProfilePage";
import SecondaryMarketPage from "./pages/customer/SecondaryMarketPage";
import PortfolioPage from "./pages/customer/PortfolioPage";
import FarmerDashboardPage from "./pages/farmer/FarmerDashboardPage";
import AdminSecurityPage from "./pages/admin/AdminSecurityPage";
import AdminConsolePage from "./pages/admin/AdminConsolePage";
import AdminOrdersPage from "./pages/admin/AdminOrdersPage";
import AdminProfilePage from "./pages/admin/AdminProfilePage";
import ManageAdminsPage from "./pages/admin/ManageAdminsPage";
import NotFoundPage from "./pages/NotFoundPage";

// ─── ROUTER ───────────────────────────────────────────────────

const FULL_BLEED_PATHS = ["/", "/home"];

function Router() {
  const location = useLocation();
  const isFullBleed = FULL_BLEED_PATHS.includes(location.pathname);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.paper,
        fontFamily: "'Crimson Text', Georgia, serif",
      }}
    >
      <style>{`
        :root {
          --ink: #F1F5F2;
          --ink-soft: #C7D1C9;
          --muted: #8FA396;
          --faint: #3D4A42;
          --border: rgba(255,255,255,0.10);
          --surface: #0D1F16;
          --paper: #0A1410;
          --white: #0A1410;
          --gold: #5FCB8C;
          --gold-light: rgba(95,203,140,0.12);
          --gold-hover: #7ADCA0;
          --shadow-soft: 0 8px 24px rgba(0,0,0,0.4);
          --shadow-medium: 0 12px 32px rgba(0,0,0,0.5);
        }
        [data-theme="light"] {
          --ink: #1E2E27;
          --ink-soft: #3C544A;
          --muted: #4C6A5D;
          --faint: #E7E0CF;
          --border: rgba(30,46,39,0.14);
          --surface: #FFFDF7;
          --paper: #F6F1E4;
          --white: #FFFFFF;
          --gold: #1E9E6B;
          --gold-light: rgba(30,158,107,0.12);
          --gold-hover: #17875A;
          --shadow-soft: 0 8px 24px rgba(30,46,39,0.10);
          --shadow-medium: 0 12px 32px rgba(30,46,39,0.14);
        }
        body { transition: background-color .3s ease, color .3s ease; }
        * { box-sizing: border-box; }
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,600;0,700;0,800;1,600&display=swap');
        body { margin: 0; font-family: 'DM Sans', sans-serif; background: ${T.paper}; color: ${T.ink}; }
        @keyframes slideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes chipPulse { from { transform: translateY(0); } to { transform: translateY(-2px); } }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: ${T.paper}; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 3px; }
        * { scrollbar-width: thin; scrollbar-color: ${T.faint} transparent; }
        input::placeholder { color: ${T.muted}; }
        .shop-chip { transition: transform .18s ease, box-shadow .18s ease; }
        .shop-chip:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.06); }
        .trend-card { transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
        .trend-card:hover { transform: translateY(-3px); box-shadow: ${T.shadowSoft}; border-color: ${T.gold}; }
        .product-card-shell { animation: slideUp .3s ease both; }
        @media (min-width: 900px) {
          .mobile-shop-actions { display: none !important; }
        }
        .page-offset { padding-top: 140px; }
        @media (max-width: 900px) {
          .page-offset { padding-top: 100px; }
        }
      `}</style>
      <ScrollToTop />
      <Navbar />
      <div className={isFullBleed ? undefined : "page-offset"}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/lookbook" element={<ShopPage />} />
        <Route path="/advice" element={<AdvicePage />} />
        <Route path="/virtual-tryon" element={<VirtualTryOnPage />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/register" element={<AuthPage mode="register" />} />
        <Route path="/admin-login" element={<AuthPage mode="admin-login" />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route path="/market" element={<SecondaryMarketPage />} />

        <Route element={<RequireAuth />}>
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
        </Route>

        <Route element={<RequireFarmer />}>
          <Route path="/farmer" element={<FarmerDashboardPage />} />
        </Route>

        <Route element={<RequireAdmin />}>
          <Route path="/admin" element={<AdminConsolePage />} />
          <Route path="/admin/orders" element={<AdminOrdersPage />} />
          <Route path="/admin/security" element={<AdminSecurityPage />} />
          <Route path="/admin/profile" element={<AdminProfilePage />} />
        </Route>

        <Route element={<RequireSuperAdmin />}>
          <Route path="/admin/manage" element={<ManageAdminsPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </div>
      <Footer />
      <CartDrawer />
    </div>
  );
}

// ─── APP ROOT ────────────────────────────────────────────────
export default function App() {
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    let mounted = true;
    apiFetch("/payments/config")
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (!mounted || !cfg) return;
        const enabled = Boolean(cfg.stripeEnabled && cfg.publishableKey);
        setStripeEnabled(enabled);
        setStripePromise(enabled ? loadStripe(cfg.publishableKey) : null);
      })
      .catch(() => {
        if (!mounted) return;
        setStripeEnabled(false);
        setStripePromise(null);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <ThemeProvider>
    <Elements stripe={stripePromise}>
      <PaymentContext.Provider value={{ stripeEnabled }}>
        <ToastProvider>
          <AuthProvider>
            <CartProvider>
              <WishlistProvider>
                <Router />
              </WishlistProvider>
            </CartProvider>
          </AuthProvider>
        </ToastProvider>
      </PaymentContext.Provider>
    </Elements>
    </ThemeProvider>
  );
}
